import { useSelector } from "react-redux";
import { ACTION } from "src/util/utils";
import { MODULE_KEY } from "./action";
import { useEffect, useState } from "react";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Tooltip } from "@mui/material";
import { SaveOutlined } from "@mui/icons-material";
import Button from "src/components/common/Button/Button";
import { formatDate } from "src/util/date";

export default function ReplenishmentJobUpdate(updateModal: any) {
    const [updateData, setupdateData] = useState<any[]>([]);
    const { data, loading } = useSelector(
        (state: any) => state[`${MODULE_KEY}${ACTION.STATE_KEY}`]
    );
    const [errors, setErrors] = useState<{ [id: number]: { [field: string]: string } }>({});
    const setFieldError = (id: number, field: string, message: string) => {
        setErrors(prev => ({
            ...prev,
            [id]: {
                ...prev[id],
                [field]: message
            }
        }));
    };
    const updateFieldValue = (id: number, field: string, value: any | null) => {
        setupdateData((prev: any[]) => {
            const index = prev.findIndex((item) => item.Id === id);
            if (index === -1) {
                return [...prev, { Id: id, [field]: value }];
            } else {
                const updated = [...prev];
                updated[index] = {
                    ...updated[index],
                    [field]: value,
                };
                return updated;
            }
        });
    };

    const updateError = (rowId: string, field: string, message: string) => {
        setErrors((prev: any) => ({
            ...prev,
            [rowId]: { ...(prev?.[rowId] || {}), [field]: message },
        }));
    };

    const clearError = (rowId: string, field: string) => {
        setErrors((prev: any) => {
            const updatedRow = { ...(prev?.[rowId] || {}) };
            delete updatedRow[field];
            return { ...prev, [rowId]: updatedRow };
        });
    };

    const columns = [
        {
            header: "REPLENISHMENT TYPE",
            accessorKey: "ReplenishmentType",
            enableColumnFilter: false,
            enableSorting: false,
            enableEditing: false,
            size: 50,
        },
        {
            header: "START DATE",
            accessorKey: "StartDate",
            size: 50,
            enableColumnFilter: false,
            enableSorting: false,
            editingMode: "cell",
            Cell: ({ cell, row }: { cell: any; row: any }) => {
                const rowId = row.original.Id;
                const updatedRow = updateData.find((item: any) => item.Id === rowId);
                const displayValue = updatedRow && 'StartDate' in updatedRow
                    ? formatDate(updatedRow.StartDate)
                    : formatDate(row.original?.StartDate);
                return displayValue;
            },
            Edit: ({ cell, row }: { cell: any; row: any }) => {
                const rowId = row.original.Id;
                const updatedItem = updateData.find((item: any) => item.Id === rowId);
                const hasUpdatedStartDate = updatedItem && 'StartDate' in updatedItem;
                const rawValue = hasUpdatedStartDate ? updatedItem.StartDate : row.original.StartDate;
                const currentValue = rawValue ? new Date(rawValue) : null;
                const [startDateValue, setStartDateValue] = useState<Date | null>(currentValue);
                const endDateRaw = updatedItem?.EndDate ?? row.original?.EndDate;
                const errorText = errors?.[rowId]?.['StartDate'];
        
                useEffect(() => {
                    setStartDateValue(currentValue);
                }, [currentValue]);
        
                return (
                    <div>
                        <DatePicker
                            format="yyyy-MM-dd"
                            value={startDateValue}
                            onChange={(date: Date | null) => {
                                setStartDateValue(date);
                                updateFieldValue(rowId, 'StartDate', date);
        
                                if (!date) {
                                    updateError(rowId, 'StartDate', 'Start Date is required');
                                } else {
                                    clearError(rowId, 'StartDate');
                                }
        
                                if (date && endDateRaw && new Date(date) > new Date(endDateRaw)) {
                                    updateFieldValue(rowId, 'EndDate', null);
                                }
                            }}
                            slotProps={{
                                textField: {
                                    variant: 'standard',
                                    fullWidth: true,
                                    error: !!errorText,
                                    helperText: errorText,
                                },
                                field: {
                                    clearable: true,
                                },
                            }}
                        />
                    </div>
                );
            },
        },
        {
            header: "END DATE",
            accessorKey: "EndDate",
            size: 50,
            enableColumnFilter: false,
            enableSorting: false,
            editingMode: "cell",
            Cell: ({ cell, row }: { cell: any; row: any }) => {
                const rowId = row.original.Id;
                const updatedRow = updateData.find((item: any) => item.Id === rowId);
                const displayValue = updatedRow && 'EndDate' in updatedRow
                    ? formatDate(updatedRow.EndDate)
                    : formatDate(row.original?.EndDate);
                return displayValue;
            },
            Edit: ({ cell, row }: { cell: any; row: any }) => {
                const rowId = row.original.Id;
                const updatedItem = updateData.find((item: any) => item.Id === rowId);
                const hasUpdatedEndDate = updatedItem && 'EndDate' in updatedItem;
                const rawValue = hasUpdatedEndDate ? updatedItem.EndDate : row.original.EndDate;
                const currentValue = rawValue ? new Date(rawValue) : null;
                const [endDateValue, setEndDateValue] = useState<Date | null>(currentValue);
                const startDateRaw = updatedItem?.StartDate ?? row.original?.StartDate;
                const errorText = errors?.[rowId]?.['EndDate'];

                useEffect(() => {
                    setEndDateValue(currentValue);
                }, [currentValue]);

                return (
                    <div>
                        <DatePicker
                            format="yyyy-MM-dd"
                            value={endDateValue}
                            minDate={startDateRaw ? new Date(startDateRaw) : undefined}
                            onChange={(date: Date | null) => {
                                setEndDateValue(date);
                                updateFieldValue(rowId, 'EndDate', date);

                                if (!date) {
                                    updateError(rowId, 'EndDate', 'End Date is required');
                                } else {
                                    clearError(rowId, 'EndDate');
                                }
                            }}
                            slotProps={{
                                textField: {
                                    variant: 'standard',
                                    fullWidth: true,
                                    error: !!errorText,
                                    helperText: errorText,
                                },
                                field: {
                                    clearable: true,
                                },
                            }}
                        />
                    </div>
                );
            },
        },
        {
            accessorKey: 'actions',
            header: 'ACTION',
            size: 150,
            enableColumnFilter: false,
            enableSorting: false,
            Cell: ({ cell, row }: { cell: any, row: any }) => {
              const rowId = row.original.Id;
              const updatedItem = updateData.find(item => item.Id === rowId);
              const startDate = updatedItem?.StartDate ?? row.original.StartDate;
              const parsedStartDate = new Date(startDate);
              const endDate = updatedItem?.EndDate ?? row.original.EndDate;
              const parsedEndDate = new Date(endDate);
              const rowErrors = errors?.[rowId] || {};
              const hasStartDateError = !!rowErrors['StartDate'] || !startDate || isNaN(parsedStartDate.getTime());
              const hasEndDateError = !!rowErrors['EndDate'] || !endDate || isNaN(parsedEndDate.getTime());
              const disableSave = hasStartDateError || hasEndDateError;
          
              return (
                <div className="d-flex flex-column align-items-start w-100">
                  <div className="d-flex w-100">
                    <Tooltip placement="top" title={disableSave ? "Fix errors before saving" : "Update"}>
                      <span> {}
                        <Button
                          icon={
                            <SaveOutlined
                              sx={{ fontSize: 20, marginLeft: "10px", marginRight: "10px" }}
                            />
                          }
                          onClick={() => {
                            if (disableSave) return; 
          
                            setFieldError(rowId, 'StartDate', '');
                            setFieldError(rowId, 'EndDate', '');
          
                            updateModal(row.original);
                          }}
                          disabled={disableSave}
                          children={undefined}
                        />
                      </span>
                    </Tooltip>
                  </div>
                </div>
              );
            }
          }
    ]

    return { columns, data, updateData, loading };

}